import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import useReviewSystemV3Experiment from '@src/quince-site-common/src/hooks/useReviewSystemV3Experiment'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import ProductReviewHighLight, { ProductReviewHighLightProps } from '../productReviewHighlight'

import { MAX_REVIEW_HIGHLIGHTS } from './reviewHighLightsSection.constants'
import { showReviewSection } from './reviewHighLightsSection.helpers'
import styles from './reviewHighLightsSection.module.scss'
import stylesV2 from './reviewHighlightsSectionV2.module.scss'
import ReviewHighlightsSummary from './ReviewHighlightsSummary'

export interface ReviewHighLightsSectionProps {
  header?: string
  highLights: ProductReviewHighLightProps[]
  className?: string
  product: Product | Bundle
  newPdpv2?: boolean
  productProperties: ProductProperties
}

const ReviewHighLightsSection = ({ highLights = [], ...props }: ReviewHighLightsSectionProps) => {
  const { t } = useTranslation('common')
  const size = highLights.length
  const showAllReviews = (size > MAX_REVIEW_HIGHLIGHTS ? true : false) && !props.newPdpv2
  const arr = size > MAX_REVIEW_HIGHLIGHTS ? highLights.slice(0, MAX_REVIEW_HIGHLIGHTS) : highLights
  const finalStyles = props.newPdpv2 ? stylesV2 : styles
  const classes = classnames(finalStyles.container, props.className)

  const averageRating = props.product.productReviewSummary?.averageRating
  const reviewCount = props.product.productReviewSummary?.reviewCount
  const isHighlightSummaryVisible = props.newPdpv2

  const { showReviewSummary, showReviewSummaryAndFilterTags } = useReviewSystemV3Experiment()
  const shouldShowReviewSummary = showReviewSummary || showReviewSummaryAndFilterTags

  const getIsVisibleTrackParams = () => {
    return {
      action: 'pdp_accordion_reviews',
      value: true,
      context: {
        productId: props.product.spid,
        productName: props.product.title,
        ...(shouldShowReviewSummary && {
          review_impression_count: arr.length,
          // eslint-disable-next-line i18next/no-literal-string
          review_section_type: 'Review Highlights',
        }),
      },
    }
  }

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  return (
    <div ref={trackIsVisibleRef} className={classes}>
      <div
        className={classnames(finalStyles.container__wrapper, {
          [`${finalStyles.container__wrapper__noBorderBottom}`]: !props.product?.details,
        })}
      >
        <div
          className={classnames(finalStyles.container__wrapper__header, {
            [`${finalStyles.container__wrapper__header__withoutSummary}`]: !isHighlightSummaryVisible,
            [`${finalStyles.container__wrapper__header__withSummary}`]: isHighlightSummaryVisible,
          })}
        >
          {props.header ?? (
            <span className={finalStyles.container__wrapper__header__title}>{t('review_highlights')}</span>
          )}
          {isHighlightSummaryVisible && (
            <ReviewHighlightsSummary
              averageRating={averageRating}
              reviewCount={reviewCount}
              showReviewSection={showReviewSection}
              newPdpv2={props.newPdpv2}
            />
          )}
        </div>
        {arr.map((highLight, index) => {
          return (
            <ProductReviewHighLight
              key={index}
              {...highLight}
              className={finalStyles.container__wrapper__reviewHighLight}
              newPdpv2={props.newPdpv2}
              isLast={index === arr.length - 1}
            />
          )
        })}
        {showAllReviews && (
          <div
            className={finalStyles.container__wrapper__all}
            onClick={() => {
              showReviewSection()
              if (shouldShowReviewSummary) {
                trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
                  action: 'click_reviews',
                  value: true,
                  context: {
                    productId: props.product.spid,
                    productName: props.product.title,
                    contentContainerDisplayName: 'Review Highlights',
                    contentContainerName: 'Product Review Highlights',
                    contentContainerType: 'Reviews',
                    productProperties: props.productProperties,
                  },
                })
              }
            }}
          >
            {t('read_all_reviews')}
          </div>
        )}

        {props.newPdpv2 && (
          <>
            <div className={finalStyles.container__wrapper__seeAllReviews} onClick={showReviewSection}>
              <span>{t('see_all_reviews')}</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ReviewHighLightsSection
